import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './index.scss';

function optionLabelForValue(options, value) {
  if (!options?.length) return null;
  const opt = options.find((o) => o.value === value);
  return opt?.label ?? null;
}

const ResultCommentPublic = () => {
  const { resultId, fieldId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!resultId || !fieldId) {
      setLoading(false);
      setError('missing_params');
      return;
    }

    let cancelled = false;
    const url = `${import.meta.env.VITE_API_URL}inspection-result/${resultId}/field/${fieldId}/comment-files`;

    setLoading(true);
    setError(null);

    axios
      .get(url, {
        headers: {
          'Content-Type': 'application/json',
          AppVersion: '1.0.0',
          AppType: 'web',
          DeviceType: 'web',
        },
      })
      .then((res) => {
        if (cancelled) return;
        const inner = res.data?.data;
        if (!inner) {
          setError('empty');
          setPayload(null);
        } else {
          setPayload(inner);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError('fetch');
        setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resultId, fieldId]);

  const field = payload?.field;
  const answerSummaries = useMemo(() => {
    if (!field?.answers?.length) return [];
    return field.answers.map((value) => ({
      value,
      label: optionLabelForValue(field.options, value),
    }));
  }, [field]);

  return (
    <div className="result-comment-public">
      <div className="rcp__shell">
        {loading && (
          <div className="rcp__state rcp__state--loading" aria-busy="true">
            <div className="rcp__spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error === 'fetch' && (
          <div className="rcp__state rcp__state--error">
            <p className="rcp__state-title">Could not load this field</p>
            <p className="rcp__state-text">
              The link may be invalid or the server is unavailable. Try again
              later.
            </p>
          </div>
        )}

        {!loading && error === 'empty' && (
          <div className="rcp__state rcp__state--error">
            <p className="rcp__state-title">No data</p>
            <p className="rcp__state-text">The server returned an empty response.</p>
          </div>
        )}

        {!loading && error === 'missing_params' && (
          <div className="rcp__state rcp__state--error">
            <p className="rcp__state-title">Invalid link</p>
            <p className="rcp__state-text">Result or field id is missing.</p>
          </div>
        )}

        {!loading && !error && payload && !field && (
          <div className="rcp__state rcp__state--error">
            <p className="rcp__state-title">Unexpected response</p>
            <p className="rcp__state-text">Field data was not included.</p>
          </div>
        )}

        {!loading && !error && payload && field && (
          <>
            <header className="rcp__header">
              <p className="rcp__eyebrow">Inspection field</p>
              <h1 className="rcp__title">{payload.category_name}</h1>
            </header>

            <div className="rcp__grid">
              <section className="rcp__card rcp__card--primary">
                <div className="rcp__card-head">
                  <h2 className="rcp__card-title">{field.label}</h2>
                </div>

                {answerSummaries.length > 0 && (
                  <div className="rcp__answers">
                    <span className="rcp__label">Response</span>
                    <ul className="rcp__answer-list">
                      {answerSummaries.map(({ value, label }) => (
                        <li key={value} className="rcp__answer-chip">
                          <span className="rcp__answer-value">{value}</span>
                          {label && (
                            <span className="rcp__answer-label">{label}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {field.has_comment && field.comment_text ? (
                  <div className="rcp__comment-block">
                    <span className="rcp__label">Comment</span>
                    <p className="rcp__comment-text">{field.comment_text}</p>
                  </div>
                ) : null}

                {field.tooltip ? (
                  <div className="rcp__meta">
                    <span className="rcp__label">Note</span>
                    <p className="rcp__meta-text">{field.tooltip}</p>
                  </div>
                ) : null}
              </section>

              {(field.comment_files?.length > 0 || field.photos?.length > 0) && (
                <section className="rcp__card">
                  <h3 className="rcp__section-title">Attachments</h3>
                  {field.comment_files?.length > 0 && (
                    <div className="rcp__files">
                      {field.comment_files.map((file) => (
                        <a
                          key={file.uuid}
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rcp__file-card"
                        >
                          <div className="rcp__file-thumb">
                            <img
                              src={file.url}
                              alt=""
                              loading="lazy"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const fb =
                                  e.target.parentElement?.querySelector(
                                    '.rcp__file-fallback',
                                  );
                                fb?.classList.add('rcp__file-fallback--show');
                              }}
                            />
                            <div className="rcp__file-fallback" aria-hidden>
                              File
                            </div>
                          </div>
                          <span className="rcp__file-name" title={file.filename}>
                            {file.filename}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                  {field.photos?.length > 0 && (
                    <div className="rcp__files rcp__files--secondary">
                      {field.photos.map((photo, i) => {
                        const url =
                          typeof photo === 'string' ? photo : photo?.url;
                        if (!url) return null;
                        return (
                          <a
                            key={url || i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rcp__file-card"
                          >
                            <div className="rcp__file-thumb">
                              <img src={url} alt="" loading="lazy" />
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              {field.options?.length > 0 && (
                <section className="rcp__card rcp__card--options">
                  <h3 className="rcp__section-title">Options</h3>
                  <ul className="rcp__options">
                    {field.options.map((opt) => {
                      const selected = field.answers?.includes(opt.value);
                      return (
                        <li
                          key={opt.value}
                          className={`rcp__option${selected ? ' rcp__option--selected' : ''}`}
                        >
                          <span className="rcp__option-value">{opt.value}</span>
                          <span className="rcp__option-label">{opt.label}</span>
                          {opt.has_comment ? (
                            <span className="rcp__option-tag">comment</span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResultCommentPublic;
